import {
  CoreApi,
  CoreApiConfig,
  ImageType,
  Product,
  ProductImageDetails,
  RoomOptions
} from '@inter-ikea-storageone/range';
import { IProduct } from '@inter-ikea-kompis/types';
import { Entity } from '@inter-ikea-core/core';
import { PrimitiveType } from 'intl-messageformat';
import { ProductType } from '@inter-ikea-storageone/range';
import * as React from 'react';
import { Dispatch } from 'react';
import { Feature } from 'features/Feature';
import { RootState } from 'ducks';
import { SceneAction } from 'components/SceneMenu/SceneMenu';
import { BatchSortFn } from 'ducks/products';

export type GalleryConfigSpr = {
  id: string;
  image?: { index: number; type: ImageType };
};
export type GalleryConfig = Record<
  string,
  {
    title?: string;
    sprs: GalleryConfigSpr[];
    ageGroup?: string;
  }[]
>;

export interface GalleryItemConfig {
  spr: GalleryConfigSpr;
  hide?: boolean;
  product?: IProduct;
}

export interface GalleryGridConfig {
  items: GalleryItemConfig[];
  ageGroup?: string;
  title?: string;
}

export type Spr2VpcMap = {
  [spr: string]: string;
};

export interface FeatureConfig {
  id: string;
  feature: typeof Feature | Feature;
}

export type ProductCardIcon = {
  icon: JSX.Element | string | undefined;
  tooltip?: string;
};

export type PlannerConfig = CoreApiConfig & {
  productGroups: ProductGroup[];
  screensaverPath?: string;
  secondaryScreensaverPath?: string;
  disabledFeatures?: { assemblyInstructions: boolean };
  cameraBehavior: any;
  sprToVpcMap: Spr2VpcMap;
  features: FeatureConfig[];
  sceneActions: SceneAction[];
  roomOptions?: RoomOptions;
  landingPageConfig: LandingPageConfig;
  productTransformers?: ((products: Product[], kompisProducts: IProduct[]) => Product[])[];
};

export type LandingPageConfig = {
  rangeDesign: {
    title?: string;
    titleTextColor?: string;
    backgroundImgUrl?: string;
    backgroundColor?: string;
  };
  designFromScratch: {
    backgroundImgUrl?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  openDesign: {
    backgroundImgUrl?: string;
    textColor?: string;
    backgroundColor: string;
  };
  guide?: {
    backgroundImg: string;
    backgroundColor: string;
  };
  browseInspiration: {
    backgroundImgUrl?: string;
    backgroundColor?: string;
    galleryConfig: GalleryConfig;
  };
};

export type BehaviorProps<K extends BehaviorKey> =
  // No props
  // | 'productGroupEntered'
  // | 'productGroupExited'
  K extends 'productDeleted' | 'removeProducts'
    ? null
    : K extends 'productClicked'
    ? { targets: Product[] }
    : K extends 'productDropped'
    ? { targets: Product[]; position: { x: number; y: number } }
    : any;

export interface BehaviorParameters<K extends BehaviorKey = BehaviorKey> {
  dispatch: Dispatch<any>;
  coreApi: CoreApi;
  rootState: RootState;
  props?: BehaviorProps<K>;
  key?: K;
}

export type BehaviorCallback<K extends BehaviorKey = BehaviorKey> = (
  params: BehaviorParameters<K>
) => any;

export type BehaviorKey =
  | 'productClicked'
  | 'productDropped'
  | 'productDeleted'
  | 'removeProducts'
  | 'productGroupEntered'
  | 'productGroupExited'
  | 'subMenuEntered'
  | 'subMenuExited'
  | 'listExpanded'
  | 'listCollapsed'
  | 'selectedMenuEntered'
  | 'selectedMenuExited';

export type Behaviors<K extends BehaviorKey = BehaviorKey> = Partial<
  Record<BehaviorKey, BehaviorCallback<K>[]>
>;

// Should contain the list of required behavior params.
// Without these the app will not function properly.
export type RequiredSelectedMenuBehaviors = Behaviors & {
  productDeleted: BehaviorCallback[];
};

export type RequiredMainMenuBehaviors = Behaviors & {
  productGroupEntered: BehaviorCallback[];
  productGroupExited: BehaviorCallback[];
};

export type SelectedMenuFilter = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => boolean;

export type MainMenuProductFilter = (
  products: Product[],
  entitiesInScene: Entity[],
  coreApi: CoreApi
) => Product[];

export type SelectedMenuSort = (
  selectedProduct: Product,
  products: Product[],
  selectedIds: string[]
) => Product[];

export type Widget = {
  id: string;
  component: React.FunctionComponent;
  title?: string | ((coreApi: CoreApi) => string);
  icon?: string /* Deprecated because SKAPA doesnt allow custom icons right now */;
  iconColor?: string;
  hidden?: (coreApi: CoreApi, products: Product[]) => boolean;
  disabled?: (coreApi: CoreApi) => boolean;
};

export type SelectedMenu = {
  menuTitle: string | ((coreApi: CoreApi) => string);
  behaviors: RequiredSelectedMenuBehaviors;
  content: SelectedMenuContent[];
};

export type SelectedMenuContent = Widget | SelectedMenuProductList;

export type SelectedMenuProductList = {
  id: string;
  title?: string | ((coreApi: CoreApi) => string);
  titleValue?: Record<string, PrimitiveType>;
  cardsTitle?: string;
  cardsSubtitle?: string;
  cluster?: string[];
  hideInfoButton?: boolean;
  hidePrice?: boolean;
  behaviors: Behaviors;
  productTypes?: ProductType[];
  useMLSorting?: boolean;
  sortOrder?: SortOrder[];
  sortClusteredProducts?: (clusters: Product[][]) => Product[][];
  productCardMainImage?: ProductCardImage;
  productCardHoverImage?: ProductCardImage;
  noThumbs?: (coreApi?: CoreApi, selectedEntityId?: number) => boolean;
  hidden?: (coreApi: CoreApi, selectedEntityId: number) => boolean;
  overrideSelection?: (selectedEntity: Entity, coreApi: CoreApi) => string[];
  filters?: Filter[];
  productFilters: SelectedMenuFilter[];
  alternativeSelectedProductList?: boolean;
  noneOption?: NoneOption;
  productThumbToolTip?: string;
  productCardIcon?: ProductCardIcon | ((product: Product) => ProductCardIcon);
  selected?: (product: Product, coreApi: CoreApi) => boolean;
  disabled?: (coreApi: CoreApi) => boolean;
  additionalItemsText?: string;
};

export type SortOrder = {
  path: string;
  reverse?: boolean;
};

export interface ProductGroup {
  id: string;
  icon: string;
  productTypes: ProductType[];
  mainMenu: MainMenu;
  selectedMenu: SelectedMenu;
}

export interface MainMenu {
  menuTitle: string;
  hidden?: (coreApi?: CoreApi) => boolean;
  behaviors: RequiredMainMenuBehaviors;
  filters?: Filter[];
  sorting?: SortingConfig[];
  widgets: Widget[];
  subMenu?: SubMenu;
}

export type SubMenuBehaviors = {
  subMenuEntered: BehaviorCallback[];
  subMenuExited: BehaviorCallback[];
};

export interface SubMenuOption {
  id: string;
  title: string;
  image?: string;
  disabled?: (coreApi: CoreApi) => boolean;
  widgets: Widget[];
  behaviors?: SubMenuBehaviors;
  sorting?: SortingConfig[];
}

export enum SubMenuType {
  TABS = 'tabs',
  LIST = 'list'
}

export interface SubMenu {
  type: SubMenuType;
  title?: string;
  options: SubMenuOption[];
  initiallySelectedTab?: (coreApi: CoreApi) => string;
  hidden?: (coreApi: CoreApi) => boolean;
}

export type RequiredNoneOptionBehaviors = Behaviors & {
  removeProducts: BehaviorCallback[];
};

export interface NoneOption {
  title?: string;
  subtitle?: string;
  image?: string;
  behaviors: RequiredNoneOptionBehaviors;
  selected: (coreApi: CoreApi) => boolean;
}

export type ProductCardImage = (product: Product) => ProductImageDetails | undefined;

export interface ProductListConfig {
  cardsTitle?: string;
  cardsSubtitle?: string | ((product: Product) => string);
  mobileCardText?: string | undefined;
  productCardMainImage?: ProductCardImage;
  productCardHoverImage?: ProductCardImage;
  productFilters?: MainMenuProductFilter[];
  categories: Category[];
  behaviors: Behaviors;
  cluster: string[];
  sortOrder: SortOrder[];
  hideInfoButton?: boolean;
  hidePrice?: boolean;
  hideAvailability?: boolean;
  infoButtonAriaLabel?: string;
  sortClusteredProducts?: (clusters: Product[][]) => Product[][];
  productCardIcon?: ProductCardIcon | ((product: Product) => ProductCardIcon);
  selected?: (product: Product, coreApi: CoreApi) => boolean;
  noneOption?: NoneOption;
}

export enum FilterType {
  FILTER = 'filter',
  TOGGLE = 'toggle'
}

export interface Filter {
  component: any;
  id: string;
  title?: string;
  options?: any[];
  dividerHidden?: boolean;
  type: FilterType.FILTER | FilterType.TOGGLE;
  noToggleButton?: boolean;
}

export interface SortingConfig {
  id: string;
  title: string;
  compare?: BatchSortFn;
}

export type Toggle = {
  id: string;
  component: any;
  behaviors: Behaviors;
  options?: Array<{ text: string; id: string; productFilterType: string }>;
  cardTitle?: string;
  cardSubtitle?: string;
  productCluster?: ProductType[];
};

export type CategoryProducts = {
  category?: string;
  products: Product[];
};
export interface CategoryFilter {
  path: string; // dot-separated path to property in a Product
  value: string | number; // accepted value for property in Product
}

export interface CategoryWidget {
  component: React.FunctionComponent<{ products: Product[] }>;
  alwaysShow?: boolean; // true if widget should be displayed even if there are no products in category
  hidden?: (coreApi: CoreApi) => boolean;
}

export interface Category {
  id: string;
  categoryTitle?: string;
  cardsTitle?: string;
  widgets?: CategoryWidget[];
  cardsSubtitle?: string;
  coreProductIds?: string[][];
  products: CategoryFilter[];
  cluster?: string[];
  shouldDisableProducts?: (coreApi: CoreApi) => boolean;
  noneOption?: NoneOption;
  cardButtonAriaLabel?: string;
  noSkipButton?: boolean;
}

export type ConfigOptions = {
  featureFlags?: { [key: string]: boolean | string | undefined };
};
export interface GuideStep {
  title: string;
  subtitle?: string;
  content: React.FunctionComponent<{ onContinue?: () => void }>;
}

export interface GuideConfig {
  steps: GuideStep[];
}
